import React, { useState, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import { DataGrid } from "@mui/x-data-grid";
import CircularProgress from "@mui/material/CircularProgress";
import { Snackbar, Alert, Chip, TextField, MenuItem, Select, FormControl, InputLabel } from "@mui/material";
import axios from "../../../../network/api";
import { getConfig } from "../../../../network/auth";
import { baseUrl } from "../../../../network/constants/constants";

const UserComponent = () => {
  const [usersList, setUsersList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  // Filter states
  const [searchText, setSearchText] = useState("");
  const [filterBusinessUser, setFilterBusinessUser] = useState("all");
  const [filterSubscribed, setFilterSubscribed] = useState("all");
  const [filterPlace, setFilterPlace] = useState("all");

  const authConfig = useMemo(() => getConfig(), []);

  const fetchUsersList = () => {
    setLoading(true);
    axios
      .get(`${baseUrl}auth/users-list/`, {
        headers: {
          Authorization: authConfig.headers.Authorization,
        },
      })
      .then((response) => {
        setUsersList(response.data.users || []);
      })
      .catch((error) => {
        console.error("Users Error:", error);
        setErrorMessage("Failed to fetch users list");
        setSnackbarOpen(true);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchUsersList();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSnackbarClose = () => setSnackbarOpen(false);

  // Unique places for dropdown
  const uniquePlaces = useMemo(() => {
    const places = usersList
      .map((u) => u.place)
      .filter((p) => p && p !== "null" && p !== "undefined");
    return [...new Set(places)].sort();
  }, [usersList]);

  // Filtered list
  const filteredUsers = useMemo(() => {
    return usersList.filter((user) => {
      const search = searchText.toLowerCase();
      const matchesSearch =
        !search ||
        user.name?.toLowerCase().includes(search) ||
        user.phone?.includes(search);

      const matchesBusiness =
        filterBusinessUser === "all" ||
        (filterBusinessUser === "yes" && user.is_business_user) ||
        (filterBusinessUser === "no" && !user.is_business_user);

      const matchesSubscribed =
        filterSubscribed === "all" ||
        (filterSubscribed === "yes" && user.subscription?.is_subscribed) ||
        (filterSubscribed === "no" && !user.subscription?.is_subscribed);

      const matchesPlace =
        filterPlace === "all" || user.place === filterPlace;

      return matchesSearch && matchesBusiness && matchesSubscribed && matchesPlace;
    });
  }, [usersList, searchText, filterBusinessUser, filterSubscribed, filterPlace]);

  const columns = [
    { field: "id", headerName: "ID", width: 70 },
    { field: "name", headerName: "Name", width: 180 },
    { field: "phone", headerName: "Phone", width: 150 },
    {
      field: "place",
      headerName: "Place",
      width: 150,
      renderCell: (params) => params.value || "—",
    },
    {
      field: "is_activated",
      headerName: "Activated",
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value ? "Active" : "Inactive"}
          color={params.value ? "success" : "default"}
          size="small"
        />
      ),
    },
    {
      field: "is_business_user",
      headerName: "Business User",
      width: 140,
      renderCell: (params) => (
        <Chip
          label={params.value ? "Yes" : "No"}
          color={params.value ? "primary" : "default"}
          size="small"
        />
      ),
    },
    {
      field: "subscription",
      headerName: "Subscribed",
      width: 130,
      renderCell: (params) => (
        <Chip
          label={params.value?.is_subscribed ? "Yes" : "No"}
          color={params.value?.is_subscribed ? "success" : "default"}
          size="small"
        />
      ),
    },
    {
      field: "subscription_plan",
      headerName: "Plan",
      width: 200,
      renderCell: (params) => params.row.subscription?.plan || "—",
    },
    {
      field: "last_otp_at",
      headerName: "Last Active",
      width: 180,
      renderCell: (params) =>
        params.value
          ? new Date(params.value).toLocaleDateString("en-IN", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })
          : "—",
    },
  ];

  return (
    <>
      <Box sx={{ height: 500, width: "80%" }} className="main-div">
        <h2 style={{ marginBottom: "12px", fontSize: "18px", fontWeight: 600 }}>
          Users List
        </h2>

        {/* Filter Bar */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            mb: 2,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          {/* Name / Phone Search */}
          <TextField
            label="Search Name / Phone"
            variant="outlined"
            size="small"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            sx={{ minWidth: 200 }}
          />

          {/* Business User Filter */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Business User</InputLabel>
            <Select
              value={filterBusinessUser}
              label="Business User"
              onChange={(e) => setFilterBusinessUser(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="yes">Yes</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </Select>
          </FormControl>

          {/* Subscribed Filter */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Subscribed</InputLabel>
            <Select
              value={filterSubscribed}
              label="Subscribed"
              onChange={(e) => setFilterSubscribed(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="yes">Yes</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </Select>
          </FormControl>

          {/* Place Filter */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Place</InputLabel>
            <Select
              value={filterPlace}
              label="Place"
              onChange={(e) => setFilterPlace(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              {uniquePlaces.map((place) => (
                <MenuItem key={place} value={place}>
                  {place}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Result count */}
          <Box sx={{ ml: "auto", color: "gray", fontSize: "14px" }}>
            {filteredUsers.length} / {usersList.length} users
          </Box>
        </Box>

        {loading ? (
          <CircularProgress className="spinner-loader" />
        ) : (
          <DataGrid
            className="business-list"
            rows={filteredUsers}
            columns={columns}
            initialState={{
              pagination: { paginationModel: { pageSize: 100 } },
            }}
            pageSizeOptions={[100]}
            disableRowSelectionOnClick
          />
        )}
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleSnackbarClose} severity="error">
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default UserComponent;